import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './DetailPage.module.css';
import tableStyles from './AdminDashboard.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin, Company } from './AdminContext';
import { UserManagementModal } from './UserManagementModal';
import { useToast } from '../../contexts/ToastContext';
import { companiesAPI, usersAPI, projectsAPI } from '../../services/api';
import { User } from '../../types';

type TabId = 'overview' | 'projects' | 'users' | 'settings';

export const CompanyDetailPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { companies, projects, users, refreshData } = useAdmin();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const company = companies.find((c) => c._id === companyId);
  const companyProjects = projects.filter((p: any) =>
    (p.company?._id || p.company) === companyId
  );
  const companyUsers = users.filter((u: any) =>
    (u.company?._id || u.company) === companyId
  );

  if (!company) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#9DA6A8', marginBottom: '1rem' }}>Klant niet gevonden.</p>
        <Button onClick={() => navigate('/admin/companies')}>Terug naar klanten</Button>
      </div>
    );
  }

  const handleDeleteCompany = async () => {
    if (window.confirm('Weet je zeker dat je deze klant wilt verwijderen? Dit heeft gevolgen voor alle gekoppelde gebruikers en projecten.')) {
      try {
        await companiesAPI.delete(company._id);
        toast.success('Klant verwijderd');
        navigate('/admin/companies');
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  };

  const handleSaveUser = async (userData: any) => {
    try {
      const { projects: selectedProjects, ...userFields } = userData;
      if (editingUser) {
        const userId = editingUser.id || (editingUser as any)._id;
        await usersAPI.update(userId, userFields);
        if (selectedProjects) {
          await usersAPI.assignProjects(userId, selectedProjects);
        }
        toast.success('Gebruiker bijgewerkt');
      } else {
        const newUser = await usersAPI.create({ ...userFields, company: companyId });
        if (selectedProjects?.length > 0 && newUser?._id) {
          await usersAPI.assignProjects(newUser._id, selectedProjects);
        }
        toast.success('Gebruiker aangemaakt');
      }
      refreshData();
      setShowUserModal(false);
      setEditingUser(null);
    } catch (error: any) {
      toast.error('Opslaan mislukt', error.response?.data?.error || 'Er ging iets mis.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?')) {
      try {
        await usersAPI.delete(userId);
        toast.success('Gebruiker verwijderd');
        refreshData();
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Weet je zeker dat je dit project wilt verwijderen?')) {
      try {
        await projectsAPI.delete(projectId);
        toast.success('Project verwijderd');
        refreshData();
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  };

  const roleLabels: Record<string, string> = {
    admin: 'Admin',
    projectManager: 'Projectmanager',
    stakeholder: 'Stakeholder',
    viewer: 'Lezer',
  };

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overzicht' },
    { id: 'projects', label: 'Projecten', count: companyProjects.length },
    { id: 'users', label: 'Gebruikers', count: companyUsers.length },
    { id: 'settings', label: 'Instellingen' },
  ];

  return (
    <div>
      {/* Back link */}
      <button
        onClick={() => navigate('/admin/companies')}
        className={styles.backLink}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Terug naar klanten
      </button>

      {/* Header */}
      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderInfo}>
          {company.logo ? (
            <div className={styles.detailAvatar}>
              <img src={company.logo} alt={company.name} />
            </div>
          ) : (
            <div className={styles.avatarPlaceholder}>
              {company.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className={styles.detailTitle}>
            <h1>{company.name}</h1>
            <div className={styles.detailSubtitle}>
              <span className={`${tableStyles.statusBadge} ${tableStyles.statusActive}`}>
                {company.subscription?.plan || 'Basic'}
              </span>
              {company.contactEmail && <span>{company.contactEmail}</span>}
              {company.website && (
                <a href={company.website} target="_blank" rel="noopener noreferrer">
                  {company.website}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className={styles.detailHeaderActions}>
          <Button variant="secondary" onClick={() => navigate(`/admin/companies/${company._id}/edit`)}>
            Bewerken
          </Button>
          <button
            onClick={handleDeleteCompany}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: '8px',
              background: 'rgba(244, 67, 54, 0.05)',
              color: '#F44336',
              fontFamily: "'Open Sans', sans-serif",
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Verwijderen
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.detailTabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.detailTab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}{tab.count !== undefined ? ` (${tab.count})` : ''}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabPanel}>
        {/* ── Overzicht ───────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className={styles.infoGrid}>
            {/* Contactgegevens */}
            <div className={styles.infoCard}>
              <h3>Contactgegevens</h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Contactpersoon</span>
                <span className={styles.infoValue}>{company.contactPerson || '-'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>E-mail</span>
                <span className={styles.infoValue}>{company.contactEmail || company.email || '-'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Telefoon</span>
                <span className={styles.infoValue}>{company.contactPhone || company.phone || '-'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Website</span>
                <span className={styles.infoValue}>
                  {company.website ? (
                    <a href={company.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1E3A2F' }}>
                      {company.website}
                    </a>
                  ) : '-'}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Domein</span>
                <span className={styles.infoValue}>{company.domain || '-'}</span>
              </div>
            </div>

            {/* Abonnement */}
            <div className={styles.infoCard}>
              <h3>Abonnement</h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Plan</span>
                <span className={styles.infoValue}>{company.subscription?.plan || 'Basic'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Max projecten</span>
                <span className={styles.infoValue}>{company.subscription?.maxProjects || 3}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Max gebruikers</span>
                <span className={styles.infoValue}>{company.subscription?.maxUsers || 5}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Status</span>
                <span className={styles.infoValue}>{company.isActive !== false ? 'Actief' : 'Inactief'}</span>
              </div>
            </div>

            {/* Recente projecten preview */}
            <div className={`${styles.infoCard} ${styles.infoCardFull}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>Gekoppelde projecten</h3>
                {companyProjects.length > 0 && (
                  <button
                    onClick={() => setActiveTab('projects')}
                    style={{
                      background: 'none', border: 'none', color: '#1E3A2F',
                      fontFamily: "'Open Sans', sans-serif", fontSize: '0.875rem',
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Bekijk alle ({companyProjects.length})
                  </button>
                )}
              </div>
              {companyProjects.length === 0 ? (
                <p style={{ color: '#9DA6A8', fontFamily: "'Open Sans', sans-serif", fontSize: '0.9rem' }}>
                  Nog geen projecten gekoppeld aan deze klant.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {companyProjects.slice(0, 5).map((project: any) => (
                    <div
                      key={project._id || project.id}
                      onClick={() => navigate(`/admin/projects/${project._id || project.id}`)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.75rem 1rem', background: '#FAFAFA', borderRadius: '8px',
                        cursor: 'pointer', transition: 'background 0.15s',
                        border: '1px solid #EEEEEE',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(30,58,47,0.03)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#FAFAFA')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                          width: '8px', height: '8px', borderRadius: '50%',
                          background: project.status === 'active' ? '#4CAF50' : project.status === 'planning' ? '#FF9800' : '#9DA6A8',
                          flexShrink: 0,
                        }} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#323232', fontFamily: "'Open Sans', sans-serif" }}>
                            {project.name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#9DA6A8', fontFamily: "'Open Sans', sans-serif" }}>
                            {typeof project.location === 'object' ? project.location?.city || '' : project.location || ''}
                            {project.startDate && ` · ${new Date(project.startDate).toLocaleDateString('nl-NL')}`}
                          </div>
                        </div>
                      </div>
                      <span className={`${tableStyles.statusBadge} ${tableStyles[`status${project.status}`]}`}>
                        {project.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Projecten ──────────────────────────────────────────────── */}
        {activeTab === 'projects' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button onClick={() => navigate('/admin/projects/new')}>
                + Nieuw project
              </Button>
            </div>
            {companyProjects.length === 0 ? (
              <div className={tableStyles.emptyState}>
                <h3>Geen projecten</h3>
                <p>Deze klant heeft nog geen projecten.</p>
              </div>
            ) : (
              <div className={tableStyles.tableWrapper}>
                <table className={tableStyles.dataTable}>
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>Locatie</th>
                      <th>Status</th>
                      <th>Startdatum</th>
                      <th>Einddatum</th>
                      <th>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyProjects.map((project: any) => (
                      <tr
                        key={project._id || project.id}
                        onClick={() => navigate(`/admin/projects/${project._id || project.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td style={{ fontWeight: 600 }}>{project.name}</td>
                        <td>
                          {typeof project.location === 'object'
                            ? project.location?.city || '-'
                            : project.location || '-'}
                        </td>
                        <td>
                          <span className={`${tableStyles.statusBadge} ${tableStyles[`status${project.status}`]}`}>
                            {project.status}
                          </span>
                        </td>
                        <td>{project.startDate ? new Date(project.startDate).toLocaleDateString('nl-NL') : '-'}</td>
                        <td>{project.endDate ? new Date(project.endDate).toLocaleDateString('nl-NL') : '-'}</td>
                        <td>
                          <div className={tableStyles.actionButtons} onClick={(e) => e.stopPropagation()}>
                            <button
                              className={tableStyles.editBtn}
                              onClick={() => navigate(`/admin/projects/${project._id || project.id}/edit`)}
                            >
                              Bewerk
                            </button>
                            <button
                              className={tableStyles.viewBtn}
                              onClick={() => navigate(`/project/${project._id || project.id}/dashboard`)}
                            >
                              Open
                            </button>
                            <button
                              className={tableStyles.deleteBtn}
                              onClick={() => handleDeleteProject(project._id || project.id)}
                            >
                              Verwijder
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Gebruikers ─────────────────────────────────────────────── */}
        {activeTab === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button onClick={() => { setEditingUser(null); setShowUserModal(true); }}>
                + Nieuwe gebruiker
              </Button>
            </div>
            {companyUsers.length === 0 ? (
              <div className={tableStyles.emptyState}>
                <h3>Geen gebruikers</h3>
                <p>Deze klant heeft nog geen gebruikers.</p>
              </div>
            ) : (
              <div className={tableStyles.tableWrapper}>
                <table className={tableStyles.dataTable}>
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>E-mail</th>
                      <th>Rol</th>
                      <th>Status</th>
                      <th>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyUsers.map((user: any) => (
                      <tr key={user._id || user.id}>
                        <td style={{ fontWeight: 600 }}>
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.name || '-'}
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`${tableStyles.roleBadge} ${tableStyles[`role${user.role}`]}`}>
                            {roleLabels[user.role] || user.role}
                          </span>
                        </td>
                        <td>
                          <span className={`${tableStyles.statusBadge} ${user.isActive !== false ? tableStyles.statusActive : tableStyles.statusInactive}`}>
                            {user.isActive !== false ? 'Actief' : 'Inactief'}
                          </span>
                        </td>
                        <td>
                          <div className={tableStyles.actionButtons}>
                            <button
                              className={tableStyles.editBtn}
                              onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                            >
                              Bewerk
                            </button>
                            <button
                              className={tableStyles.deleteBtn}
                              onClick={() => handleDeleteUser(user._id || user.id)}
                            >
                              Verwijder
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Instellingen ────────────────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <h3>Contactgegevens</h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Contactpersoon</span>
                <span className={styles.infoValue}>{company.contactPerson || '-'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>E-mail</span>
                <span className={styles.infoValue}>{company.contactEmail || company.email || '-'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Telefoon</span>
                <span className={styles.infoValue}>{company.contactPhone || company.phone || '-'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Website</span>
                <span className={styles.infoValue}>
                  {company.website ? (
                    <a href={company.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1E3A2F' }}>
                      {company.website}
                    </a>
                  ) : '-'}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Domein</span>
                <span className={styles.infoValue}>{company.domain || '-'}</span>
              </div>
            </div>

            <div className={styles.infoCard}>
              <h3>Abonnement</h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Plan</span>
                <span className={styles.infoValue}>{company.subscription?.plan || 'Basic'}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Max projecten</span>
                <span className={styles.infoValue}>{company.subscription?.maxProjects || 3}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Max gebruikers</span>
                <span className={styles.infoValue}>{company.subscription?.maxUsers || 5}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Status</span>
                <span className={styles.infoValue}>{company.isActive !== false ? 'Actief' : 'Inactief'}</span>
              </div>
            </div>

            {company.settings?.features && (
              <div className={`${styles.infoCard} ${styles.infoCardFull}`}>
                <h3>Beschikbare modules</h3>
                <div className={styles.moduleBadges}>
                  {Object.entries(company.settings.features).map(([key, enabled]) => (
                    <span
                      key={key}
                      className={`${styles.moduleBadge} ${enabled ? styles.moduleBadgeActive : styles.moduleBadgeInactive}`}
                    >
                      {key}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showUserModal && (
        <UserManagementModal
          user={editingUser}
          companies={companies}
          projects={projects}
          onSave={handleSaveUser}
          onClose={() => { setShowUserModal(false); setEditingUser(null); }}
        />
      )}
    </div>
  );
};
